import { createSelector } from '@reduxjs/toolkit';

import { type RootState } from '../../../lib/store';

// Stable empty array reference to avoid unnecessary re-renders
const EMPTY_ARRAY: never[] = [];

export const selectFilterBy = (state: RootState) => state.annotation?.filterBy ?? 'all';
export const selectNoteSortBy = (state: RootState) => state.annotation?.noteSortBy ?? 'ascending';
export const selectGroupBy = (state: RootState) => state.annotation?.groupBy ?? 'none';
export const selectSelectedNoteId = (state: RootState) => state.annotation?.selectedNoteId ?? null;
export const selectAreAllConceptsExpanded = (state: RootState) =>
  state.annotation?.areAllConceptsExpanded ?? undefined;
export const selectExpandedConceptIds = (state: RootState) =>
  state.annotation?.expandedConceptIds ?? [];
export const selectConceptAnnotationIdsInEditMode = (state: RootState) =>
  state.annotation?.conceptAnnotationIdsInEditMode ?? [];
export const selectAddEvidenceModeForConceptId = (state: RootState) =>
  state.annotation?.addEvidenceModeForConceptId ?? null;
export const selectHighlightedTextSpans = (state: RootState) =>
  state.annotation?.highlightedTextSpans ?? [];
export const selectPendingSpans = (state: RootState) => state.annotation?.pendingSpans ?? {};
// Memoized selector for pending spans by annotation ID
const selectPendingSpansState = (state: RootState) => state.annotation?.pendingSpans ?? {};

export const selectPendingSpansForAnnotation = (annotationId: string) =>
  createSelector(
    [selectPendingSpansState],
    (pendingSpans) => pendingSpans[annotationId] ?? EMPTY_ARRAY
  );

export const selectViewOptionExpanded = (state: RootState) =>
  state.annotation?.viewOptionExpanded ?? false;
export const selectDeleteSpanError = (annotationId: string) => (state: RootState) =>
  state.annotation?.deleteSpanError[annotationId] ?? null;
export const selectCreateAnnotationError = (conceptId: string) => (state: RootState) =>
  state.annotation?.createAnnotationError[conceptId] ?? null;
export const selectEditAnnotationError = (annotationId: string) => (state: RootState) =>
  state.annotation?.editAnnotationError[annotationId] ?? null;
export const selectDeleteAnnotationError = (annotationId: string) => (state: RootState) =>
  state.annotation?.deleteAnnotationError[annotationId] ?? null;
export const selectTaskId = (state: RootState) => state.annotation?.taskId ?? '';
export const selectAnnotationStarted = (state: RootState) =>
  state.annotation?.annotationStarted ?? false;
export const selectEvidenceIdsToDelete = (state: RootState) =>
  state.annotation?.evidenceIdsToDelete ?? {};
export const selectDirtyAnnotationKeys = (state: RootState) =>
  state.annotation?.dirtyAnnotationKeys ?? [];
export const selectScrollToHighlightTrigger = (state: RootState) =>
  state.annotation?.scrollToHighlightTrigger ?? 0;

const ANNOTATION_MUTATION_ENDPOINTS = new Set([
  'addAnnotationSpans',
  'createAnnotation',
  'updateAnnotationAttributes',
  'deleteAnnotation',
  'bulkCreateAnnotations',
  'finalizeAnnotation',
]);

export const selectIsAnnotationMutating = (state: RootState) => {
  const hasPendingMutation = Object.values(state.annotationToolApiV1.mutations).some(
    (mutation) =>
      mutation?.status === 'pending' && ANNOTATION_MUTATION_ENDPOINTS.has(mutation.endpointName)
  );
  const hasPendingQuery = Object.values(state.annotationToolApiV1.queries).some(
    (query) => query?.status === 'pending'
  );
  return hasPendingMutation || hasPendingQuery;
};

export const selectHasUnsavedNewAnnotations = (state: RootState) =>
  state.annotation?.dirtyAnnotationKeys.some((key) => key.includes('new-')) ?? false;
